import { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { exportToExcel } from '@/utils/exportUtils';
import { formatINR } from '@/utils/format';
import { getData } from '@/services/FetchNodeServices.js';

type GSTTab = 'gstr1' | 'gstr2' | 'gstr3b';

export default function GSTReportsPage() {
  const [activeTab, setActiveTab] = useState<GSTTab>('gstr1');
  const [month, setMonth] = useState('March');
  const [year, setYear] = useState('2024');
  const [loading, setLoading] = useState(false);
  const [lastGeneratedAt, setLastGeneratedAt] = useState<string | null>(null);

  // GSTR-1 State
  const [gstr1Kpis, setGstr1Kpis] = useState({
    totalInvoices: 6,
    totalTaxable: 71488,
    totalGstCollected: 8496.47,
    b2bInvoices: 5,
  });

  const [b2bSales, setB2bSales] = useState<any[]>([
    { invoiceNo: 'INV-2024-001', date: '2024-03-18', customerGstin: '27AABCT1332L1ZT', customerName: 'Ramesh Traders', taxableValue: 2268, cgst: 137.39, sgst: 137.39, igst: null, totalGst: 274.78, invoiceTotal: 2543 },
    { invoiceNo: 'INV-2024-002', date: '2024-03-17', customerGstin: '27ABCS5432M1ZP', customerName: 'Sunrise Electronics', taxableValue: 4644, cgst: 367.56, sgst: 367.56, igst: null, totalGst: 735.12, invoiceTotal: 5379 },
    { invoiceNo: 'INV-2024-003', date: '2024-03-16', customerGstin: '07ABCD1234E1ZX', customerName: 'Delhi Distributors', taxableValue: 17913, cgst: null, sgst: null, igst: 1594.75, totalGst: 1594.75, invoiceTotal: 19508 },
    { invoiceNo: 'INV-2024-005', date: '2024-03-14', customerGstin: '29AABCN9876F1ZR', customerName: 'Metro Retailers', taxableValue: 10378, cgst: 452.88, sgst: 452.88, igst: null, totalGst: 905.76, invoiceTotal: 11282 },
    { invoiceNo: 'INV-2024-007', date: '2024-03-12', customerGstin: '27ABCG7654H1ZM', customerName: 'Gupta Wholesale', taxableValue: 32705, cgst: 2195.85, sgst: 2195.85, igst: null, totalGst: 4391.70, invoiceTotal: 37097 },
  ]);

  const [b2bSummary, setB2bSummary] = useState({
    count: 5,
    taxableValue: 67908,
    cgst: 3153.68,
    sgst: 3153.68,
    igst: 1594.75,
    totalGst: 7902.11,
    invoiceTotal: 75809,
  });

  const [b2cSales, setB2cSales] = useState<any[]>([
    { invoiceNo: 'INV-2024-004', date: '2024-03-15', customerName: 'Kumar & Sons', taxableValue: 3582, gstRate: '17%', gstAmount: 594.36, total: 4176 },
  ]);

  const [b2cSummary, setB2cSummary] = useState({
    count: 1,
    taxableValue: 3582,
    gstRate: '—',
    gstAmount: 594.36,
    total: 4176,
  });

  const [hsnSummary, setHsnSummary] = useState<any[]>([
    { hsnCode: '0902', description: 'Tata Tea Premium 500g', uom: 'Pcs', totalQty: 105, taxableValue: 15739.6, gstRate: '5%', cgst: 246.05, sgst: 246.05, igst: 294.88, totalTax: 786.98 },
    { hsnCode: '1512', description: 'Fortune Sunflower Oil 1L', uom: 'Btl', totalQty: 39, taxableValue: 6269.4, gstRate: '5%', cgst: 97.20, sgst: 97.20, igst: 119.07, totalTax: 313.47 },
    { hsnCode: '1902', description: 'Maggi 2-Minute Noodles', uom: 'Pcs', totalQty: 270, taxableValue: 4050.0, gstRate: '12%', cgst: 198.00, sgst: 198.00, igst: null, totalTax: 486.00 },
    { hsnCode: '1905', description: 'Good Day Butter 200g', uom: 'Pcs', totalQty: 60, taxableValue: 2160.0, gstRate: '5%', cgst: 32.40, sgst: 32.40, igst: null, totalTax: 108.00 },
    { hsnCode: '2501', description: 'Tata Salt 1kg', uom: 'Pcs', totalQty: 160, taxableValue: 3520.0, gstRate: '0%', cgst: null, sgst: null, igst: null, totalTax: 0 },
    { hsnCode: '3305', description: 'Clinic Plus Shampoo 80ml', uom: 'Pcs', totalQty: 78, taxableValue: 4212.0, gstRate: '18%', cgst: 233.28, sgst: 233.28, igst: 291.60, totalTax: 758.16 },
    { hsnCode: '3306', description: 'Colgate Max Fresh 150g', uom: 'Pcs', totalQty: 70, taxableValue: 5880.0, gstRate: '12%', cgst: 352.80, sgst: 352.80, igst: null, totalTax: 705.60 },
    { hsnCode: '3401', description: 'Dove Soap 100g', uom: 'Pcs', totalQty: 92, taxableValue: 5336.0, gstRate: '18%', cgst: 480.24, sgst: 480.24, igst: null, totalTax: 960.48 },
  ]);

  // GSTR-2 State
  const [gstr2Kpis, setGstr2Kpis] = useState({
    totalPurchases: 2,
    totalTaxable: 816250,
    totalItcAvailable: 146925,
    registeredSuppliers: 2,
  });

  const [registeredPurchases, setRegisteredPurchases] = useState<any[]>([
    { invoiceNo: 'PINV-2024-004', date: '2024-03-05', supplierGstin: '27AACT2727Q1ZW', supplierName: 'TechSupply Co.', taxableValue: 571600, cgst: 51444, sgst: 51444, igst: null, totalGst: 102888, eligibleItc: 102888 },
    { invoiceNo: 'PINV-2024-005', date: '2024-03-20', supplierGstin: '19MAAC11234A1Z5', supplierName: 'NexGen Components', taxableValue: 244650, cgst: 22018.5, sgst: 22018.5, igst: null, totalGst: 44037, eligibleItc: 44037 },
  ]);

  const [registeredPurchasesSummary, setRegisteredPurchasesSummary] = useState({
    count: 2,
    taxableValue: 816250,
    cgst: 73462.5,
    sgst: 73462.5,
    igst: 0,
    totalGst: 146925,
    eligibleItc: 146925,
  });

  const [importSummary, setImportSummary] = useState({
    totalPurchaseTaxable: 816250,
    totalItcAvailable: 146925,
    cgstItc: 73462.5,
    sgstItc: 73462.5,
    igstItc: 0,
  });

  // GSTR-3B State
  const [gstr3bBanner, setGstr3bBanner] = useState({
    type: 'REFUND',
    amount: 138428.53,
    amountFormatted: '₹1,38,428.53 refund due',
    subtext: 'Excess ITC available — refund or carry forward',
  });

  const [outward31, setOutward31] = useState({
    taxable: { igst: 1594.75, cgst: 3450.86, sgst: 3450.86 },
    zeroRated: 0,
    nilRated: 0,
    exempted: 0,
  });

  const [itc4, setItc4] = useState({
    importOfGoods: 0,
    fromRegisteredSuppliers: 146925,
    itcReversed: 0,
    netItcAvailable: 146925,
  });

  const [payment6, setPayment6] = useState([
    { description: 'Tax Payable', igst: 1594.75, cgst: 3450.86, sgst: 3450.86 },
    { description: 'Less: ITC Available', igst: 0, cgst: 73462.5, sgst: 73462.5 },
  ]);

  // Dynamic fetch function
  const handleRegenerate = useCallback(async (selectedMonth = month, selectedYear = year) => {
    try {
      setLoading(true);
      const res = await getData('api/v1/reports/gst', {
        month: selectedMonth,
        year: selectedYear,
      });

      if (res?.success && res?.data) {
        const d = res.data;

        // 1. GSTR-1
        if (d.gstr1) {
          if (d.gstr1.kpis) setGstr1Kpis(d.gstr1.kpis);
          if (Array.isArray(d.gstr1.b2bSales)) setB2bSales(d.gstr1.b2bSales);
          if (d.gstr1.b2bSummary) setB2bSummary(d.gstr1.b2bSummary);
          if (Array.isArray(d.gstr1.b2cSales)) setB2cSales(d.gstr1.b2cSales);
          if (d.gstr1.b2cSummary) setB2cSummary(d.gstr1.b2cSummary);
          if (Array.isArray(d.gstr1.hsnSummary)) setHsnSummary(d.gstr1.hsnSummary);
        }

        // 2. GSTR-2
        if (d.gstr2) {
          if (d.gstr2.kpis) setGstr2Kpis(d.gstr2.kpis);
          if (Array.isArray(d.gstr2.registeredPurchases)) setRegisteredPurchases(d.gstr2.registeredPurchases);
          if (d.gstr2.registeredSummary) setRegisteredPurchasesSummary(d.gstr2.registeredSummary);
          if (d.gstr2.importSummary) setImportSummary(d.gstr2.importSummary);
        }

        // 3. GSTR-3B
        if (d.gstr3b) {
          if (d.gstr3b.banner) setGstr3bBanner(d.gstr3b.banner);
          if (d.gstr3b.outward31) setOutward31(d.gstr3b.outward31);
          if (d.gstr3b.itc4) setItc4(d.gstr3b.itc4);
          if (Array.isArray(d.gstr3b.payment6)) setPayment6(d.gstr3b.payment6);
        }

        setLastGeneratedAt(d.generatedAt || new Date().toLocaleTimeString());
      }
    } catch (err) {
      console.warn('Error fetching GST report data:', err);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    handleRegenerate();
  }, []);

  // Quick Preset Helper
  const applyPreset = (m: string, y: string) => {
    setMonth(m);
    setYear(y);
    handleRegenerate(m, y);
  };

  // Export JSON (for GST Portal)
  const handleExportJSON = () => {
    const payload = {
      gstin: '27AABCU9603R1ZX',
      fp: `${month.substring(0, 2).toUpperCase()}${year}`,
      version: 'GST_OFFLINE_TOOL_v1.4',
      gstr1: {
        b2b: b2bSales.map(r => ({
          ctin: r.customerGstin,
          inv: [{ inum: r.invoiceNo, idt: r.date, val: r.invoiceTotal, pos: '27', rchrg: 'N', inv_typ: 'R', itms: [{ rt: 18, txval: r.taxableValue, iamt: r.igst || 0, camt: r.cgst || 0, samt: r.sgst || 0 }] }]
        })),
        b2cs: b2cSales.map(r => ({
          pos: '27', txval: r.taxableValue, rt: 17, camt: r.gstAmount / 2, samt: r.gstAmount / 2, typ: 'OE'
        })),
        hsn: { data: hsnSummary.map((h, i) => ({ num: i + 1, hsn_sc: h.hsnCode, desc: h.description, uqc: h.uom, qty: h.totalQty, txval: h.taxableValue, rt: 18, iamt: h.igst || 0, camt: h.cgst || 0, samt: h.sgst || 0 })) }
      },
      gstr2: {
        b2b: registeredPurchases.map(r => ({
          ctin: r.supplierGstin, inum: r.invoiceNo, idt: r.date, val: r.taxableValue + r.totalGst, txval: r.taxableValue, camt: r.cgst || 0, samt: r.sgst || 0, itc_elg: 'Y'
        }))
      },
      gstr3b: {
        sup_details: outward31,
        itc_elg: itc4,
        tax_pmt: payment6
      }
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GST_${activeTab.toUpperCase()}_${month}_${year}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export Excel
  const handleExportExcel = () => {
    if (activeTab === 'gstr1') {
      exportToExcel(b2bSales, [
        { header: 'Invoice No', key: 'invoiceNo' },
        { header: 'Date', key: 'date' },
        { header: 'Customer GSTIN', key: 'customerGstin' },
        { header: 'Customer Name', key: 'customerName' },
        { header: 'Taxable Value', key: 'taxableValue' },
        { header: 'CGST', key: 'cgst' },
        { header: 'SGST', key: 'sgst' },
        { header: 'IGST', key: 'igst' },
        { header: 'Total GST', key: 'totalGst' },
        { header: 'Invoice Total', key: 'invoiceTotal' },
      ], `GSTR1_B2B_${month}_${year}`);
    } else if (activeTab === 'gstr2') {
      exportToExcel(registeredPurchases, [
        { header: 'Invoice No', key: 'invoiceNo' },
        { header: 'Date', key: 'date' },
        { header: 'Supplier GSTIN', key: 'supplierGstin' },
        { header: 'Supplier Name', key: 'supplierName' },
        { header: 'Taxable Value', key: 'taxableValue' },
        { header: 'CGST', key: 'cgst' },
        { header: 'SGST', key: 'sgst' },
        { header: 'IGST', key: 'igst' },
        { header: 'Total GST', key: 'totalGst' },
        { header: 'Eligible ITC', key: 'eligibleItc' },
      ], `GSTR2_Inward_${month}_${year}`);
    } else {
      exportToExcel([
        { Metric: 'Net GST Payable / Refund', Value: gstr3bBanner.amountFormatted },
        { Metric: 'Outward IGST', Value: outward31.taxable.igst },
        { Metric: 'Outward CGST', Value: outward31.taxable.cgst },
        { Metric: 'Outward SGST', Value: outward31.taxable.sgst },
        { Metric: 'Eligible ITC', Value: itc4.netItcAvailable },
      ], [
        { header: 'Metric', key: 'Metric' },
        { header: 'Value', key: 'Value' },
      ], `GSTR3B_Summary_${month}_${year}`);
    }
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Breadcrumb */}
        <div className="text-[11px] font-medium text-[#64748b]">Page</div>

        {/* Top Header & Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#1e293b]">GST Reports</h2>
            <p className="text-xs text-[#64748b] mt-0.5">GSTR filing, reconciliation, and GST analytics</p>
          </div>

          <div className="flex items-center flex-wrap gap-2 text-xs">
            {/* Month Dropdown */}
            <div className="flex flex-col">
              <span className="text-[10px] text-[#64748b] font-medium mb-0.5">Month</span>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="h-8 px-2.5 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] font-medium focus:outline-none focus:border-[#4f46e5] cursor-pointer"
              >
                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {/* Year Dropdown */}
            <div className="flex flex-col">
              <span className="text-[10px] text-[#64748b] font-medium mb-0.5">Year</span>
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="h-8 px-2.5 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] font-medium focus:outline-none focus:border-[#4f46e5] cursor-pointer"
              >
                {['2024', '2025', '2026', '2027'].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {/* GSTIN Pill */}
            <div className="flex flex-col justify-end">
              <div className="h-8 px-3 rounded-lg border border-[#e2e8f0] bg-slate-50/70 text-xs font-mono font-semibold text-[#334155] flex items-center gap-1.5 shadow-2xs">
                <i className="ri-building-line text-[#64748b]" />
                <span>27AABCU9603R1ZX</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 mt-auto">
              {/* Regenerate Button */}
              <button
                id="btn-regenerate-gst"
                onClick={() => handleRegenerate()}
                disabled={loading}
                className="h-8 px-4 rounded-lg bg-[#4f46e5] text-white font-medium hover:bg-indigo-700 active:scale-95 transition-all flex items-center gap-1.5 shadow-sm cursor-pointer disabled:opacity-60"
              >
                <i className={`text-xs ${loading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}`} />
                <span>{loading ? 'Regenerating...' : 'Regenerate'}</span>
              </button>

              {/* Export JSON Button */}
              <button
                onClick={handleExportJSON}
                className="h-8 px-3 rounded-lg bg-[#ecfdf5] border border-[#a7f3d0] text-[#059669] hover:bg-[#d1fae5] font-medium flex items-center gap-1.5 transition-colors cursor-pointer"
                title="Export GST Portal JSON offline payload"
              >
                <i className="ri-file-code-line text-xs" />
                <span>Export JSON</span>
              </button>

              {/* Export Excel Button */}
              <button
                onClick={handleExportExcel}
                className="h-8 px-3 rounded-lg bg-[#eff6ff] border border-[#bfdbfe] text-[#2563eb] hover:bg-[#dbeafe] font-medium flex items-center gap-1.5 transition-colors cursor-pointer"
                title="Export report to Excel / CSV"
              >
                <i className="ri-file-excel-2-line text-xs" />
                <span>Export Excel</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Date Presets */}
        <div className="flex items-center gap-2 flex-wrap text-xs bg-slate-50/80 p-2 rounded-lg border border-[#e2e8f0]">
          <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wide flex items-center gap-1">
            <i className="ri-flashlight-line text-indigo-600" /> Presets:
          </span>
          <button
            type="button"
            onClick={() => applyPreset('March', '2024')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
              month === 'March' && year === '2024'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-white text-[#475569] hover:bg-slate-100 border border-[#e2e8f0]'
            }`}
          >
            March 2024 (Screenshot Demo)
          </button>
          <button
            type="button"
            onClick={() => applyPreset('August', '2026')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
              month === 'August' && year === '2026'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-white text-[#475569] hover:bg-slate-100 border border-[#e2e8f0]'
            }`}
          >
            August 2026 (Live Database)
          </button>
          <button
            type="button"
            onClick={() => applyPreset('October', '2026')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer ${
              month === 'October' && year === '2026'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-white text-[#475569] hover:bg-slate-100 border border-[#e2e8f0]'
            }`}
          >
            October 2026 (Current Month)
          </button>
          {lastGeneratedAt && (
            <span className="ml-auto text-[11px] text-emerald-700 flex items-center gap-1">
              <i className="ri-checkbox-circle-fill text-emerald-600" />
              Generated at: <strong>{lastGeneratedAt}</strong>
            </span>
          )}
        </div>

        {/* GST Navigation Tabs */}
        <div className="flex items-center gap-2 border-b border-[#e2e8f0] pb-3">
          <button
            onClick={() => setActiveTab('gstr1')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'gstr1'
                ? 'bg-white text-[#1e293b] border border-[#cbd5e1] shadow-xs'
                : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            GSTR-1 (Outward)
          </button>
          <button
            onClick={() => setActiveTab('gstr2')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'gstr2'
                ? 'bg-white text-[#1e293b] border border-[#cbd5e1] shadow-xs'
                : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            GSTR-2 (Inward)
          </button>
          <button
            onClick={() => setActiveTab('gstr3b')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'gstr3b'
                ? 'bg-white text-[#1e293b] border border-[#cbd5e1] shadow-xs'
                : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            GSTR-3B (Summary)
          </button>
        </div>

        {/* ========================================================================= */}
        {/* TAB 1: GSTR-1 (Outward)                                                   */}
        {/* ========================================================================= */}
        {activeTab === 'gstr1' && (
          <div className="space-y-6 animate-fade-in">
            {/* Top 4 KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between">
                <div className="flex items-center gap-2 text-[#64748b] text-xs font-medium">
                  <i className="ri-file-text-line text-indigo-600 bg-indigo-50 p-1.5 rounded-md text-sm" />
                  <span>Total Invoices</span>
                </div>
                <div className="text-2xl font-bold text-[#1e293b] mt-2">{gstr1Kpis.totalInvoices}</div>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between">
                <div className="flex items-center gap-2 text-[#64748b] text-xs font-medium">
                  <i className="ri-money-rupee-circle-line text-emerald-600 bg-emerald-50 p-1.5 rounded-md text-sm" />
                  <span>Total Taxable</span>
                </div>
                <div className="text-2xl font-bold text-[#1e293b] mt-2">{formatINR(gstr1Kpis.totalTaxable)}</div>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between">
                <div className="flex items-center gap-2 text-[#64748b] text-xs font-medium">
                  <i className="ri-wallet-3-line text-amber-600 bg-amber-50 p-1.5 rounded-md text-sm" />
                  <span>Total GST Collected</span>
                </div>
                <div className="text-2xl font-bold text-[#1e293b] mt-2">{formatINR(gstr1Kpis.totalGstCollected)}</div>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between">
                <div className="flex items-center gap-2 text-[#64748b] text-xs font-medium">
                  <i className="ri-community-line text-sky-600 bg-sky-50 p-1.5 rounded-md text-sm" />
                  <span>B2B Invoices</span>
                </div>
                <div className="text-2xl font-bold text-[#1e293b] mt-2">{gstr1Kpis.b2bInvoices}</div>
              </div>
            </div>

            {/* 1. B2B Sales (Registered Customers) */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
                <h3 className="text-sm font-bold text-[#1e293b]">B2B Sales (Registered Customers)</h3>
                <button
                  onClick={handleExportExcel}
                  className="h-7 px-2.5 rounded border border-[#e2e8f0] bg-white text-[11px] text-[#475569] hover:bg-slate-50 flex items-center gap-1 cursor-pointer"
                >
                  <i className="ri-download-line text-xs" />
                  <span>Export CSV</span>
                </button>
              </div>

              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">
                      <th className="px-4 py-3">Invoice No</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Customer GSTIN</th>
                      <th className="px-4 py-3">Customer Name</th>
                      <th className="px-4 py-3 text-right">Taxable Value</th>
                      <th className="px-4 py-3 text-right">CGST</th>
                      <th className="px-4 py-3 text-right">SGST</th>
                      <th className="px-4 py-3 text-right">IGST</th>
                      <th className="px-4 py-3 text-right">Total GST</th>
                      <th className="px-4 py-3 text-right">Invoice Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {b2bSales.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-4 py-3 font-medium text-[#4f46e5]">{r.invoiceNo}</td>
                        <td className="px-4 py-3 text-[#64748b]">{r.date}</td>
                        <td className="px-4 py-3 font-mono text-[#334155]">{r.customerGstin}</td>
                        <td className="px-4 py-3 font-medium text-[#1e293b]">{r.customerName}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#1e293b]">{formatINR(r.taxableValue)}</td>
                        <td className="px-4 py-3 text-right font-medium text-blue-600">{r.cgst ? formatINR(r.cgst) : '—'}</td>
                        <td className="px-4 py-3 text-right font-medium text-purple-600">{r.sgst ? formatINR(r.sgst) : '—'}</td>
                        <td className="px-4 py-3 text-right font-medium text-amber-600">{r.igst ? formatINR(r.igst) : '—'}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#1e293b]">{formatINR(r.totalGst)}</td>
                        <td className="px-4 py-3 text-right font-bold text-[#1e293b]">{formatINR(r.invoiceTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#f8fafc] border-t-2 border-[#e2e8f0] font-bold text-xs text-[#1e293b]">
                      <td className="px-4 py-3" colSpan={4}>B2B TOTAL ({b2bSummary.count} invoices)</td>
                      <td className="px-4 py-3 text-right">{formatINR(b2bSummary.taxableValue)}</td>
                      <td className="px-4 py-3 text-right text-blue-600">{formatINR(b2bSummary.cgst)}</td>
                      <td className="px-4 py-3 text-right text-purple-600">{formatINR(b2bSummary.sgst)}</td>
                      <td className="px-4 py-3 text-right text-amber-600">{formatINR(b2bSummary.igst)}</td>
                      <td className="px-4 py-3 text-right">{formatINR(b2bSummary.totalGst)}</td>
                      <td className="px-4 py-3 text-right">{formatINR(b2bSummary.invoiceTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* 2. B2C Sales (Retail / Unregistered Customers) */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-[#e2e8f0]">
                <h3 className="text-sm font-bold text-[#1e293b]">B2C Sales (Retail / Unregistered Customers)</h3>
              </div>

              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">
                      <th className="px-4 py-3">Invoice No</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Customer Name</th>
                      <th className="px-4 py-3 text-right">Taxable Value</th>
                      <th className="px-4 py-3 text-center">GST Rate</th>
                      <th className="px-4 py-3 text-right">GST Amount</th>
                      <th className="px-4 py-3 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {b2cSales.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-4 py-3 font-medium text-[#4f46e5]">{r.invoiceNo}</td>
                        <td className="px-4 py-3 text-[#64748b]">{r.date}</td>
                        <td className="px-4 py-3 font-medium text-[#1e293b]">{r.customerName}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#1e293b]">{formatINR(r.taxableValue)}</td>
                        <td className="px-4 py-3 text-center text-[#64748b]">{r.gstRate}</td>
                        <td className="px-4 py-3 text-right font-medium text-emerald-600">{formatINR(r.gstAmount)}</td>
                        <td className="px-4 py-3 text-right font-bold text-[#1e293b]">{formatINR(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#f8fafc] border-t-2 border-[#e2e8f0] font-bold text-xs text-[#1e293b]">
                      <td className="px-4 py-3" colSpan={3}>B2C TOTAL ({b2cSummary.count} invoices)</td>
                      <td className="px-4 py-3 text-right">{formatINR(b2cSummary.taxableValue)}</td>
                      <td className="px-4 py-3 text-center text-[#64748b]">{b2cSummary.gstRate}</td>
                      <td className="px-4 py-3 text-right text-emerald-600">{formatINR(b2cSummary.gstAmount)}</td>
                      <td className="px-4 py-3 text-right">{formatINR(b2cSummary.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* 3. HSN-wise Summary */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-[#e2e8f0]">
                <h3 className="text-sm font-bold text-[#1e293b]">HSN-wise Summary</h3>
              </div>

              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">
                      <th className="px-4 py-3">HSN Code</th>
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3 text-center">UOM</th>
                      <th className="px-4 py-3 text-center">Total Qty</th>
                      <th className="px-4 py-3 text-right">Taxable Value</th>
                      <th className="px-4 py-3 text-center">GST Rate</th>
                      <th className="px-4 py-3 text-right">CGST</th>
                      <th className="px-4 py-3 text-right">SGST</th>
                      <th className="px-4 py-3 text-right">IGST</th>
                      <th className="px-4 py-3 text-right">Total Tax</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {hsnSummary.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-4 py-3 font-mono font-bold text-[#1e293b]">{r.hsnCode}</td>
                        <td className="px-4 py-3 text-[#334155]">{r.description}</td>
                        <td className="px-4 py-3 text-center text-[#64748b]">{r.uom}</td>
                        <td className="px-4 py-3 text-center font-medium text-[#1e293b]">{r.totalQty}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#1e293b]">{formatINR(r.taxableValue)}</td>
                        <td className="px-4 py-3 text-center text-[#64748b]">{r.gstRate}</td>
                        <td className="px-4 py-3 text-right font-medium text-blue-600">{r.cgst ? formatINR(r.cgst) : '—'}</td>
                        <td className="px-4 py-3 text-right font-medium text-purple-600">{r.sgst ? formatINR(r.sgst) : '—'}</td>
                        <td className="px-4 py-3 text-right font-medium text-amber-600">{r.igst ? formatINR(r.igst) : '—'}</td>
                        <td className="px-4 py-3 text-right font-bold text-[#1e293b]">{formatINR(r.totalTax)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: GSTR-2 (Inward)                                                    */}
        {/* ========================================================================= */}
        {activeTab === 'gstr2' && (
          <div className="space-y-6 animate-fade-in">
            {/* Top 4 KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between">
                <div className="flex items-center gap-2 text-[#64748b] text-xs font-medium">
                  <i className="ri-receipt-line text-indigo-600 bg-indigo-50 p-1.5 rounded-md text-sm" />
                  <span>Total Purchases</span>
                </div>
                <div className="text-2xl font-bold text-[#1e293b] mt-2">{gstr2Kpis.totalPurchases}</div>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between">
                <div className="flex items-center gap-2 text-[#64748b] text-xs font-medium">
                  <i className="ri-money-rupee-circle-line text-emerald-600 bg-emerald-50 p-1.5 rounded-md text-sm" />
                  <span>Total Taxable</span>
                </div>
                <div className="text-2xl font-bold text-[#1e293b] mt-2">{formatINR(gstr2Kpis.totalTaxable)}</div>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between">
                <div className="flex items-center gap-2 text-[#64748b] text-xs font-medium">
                  <i className="ri-shield-check-line text-blue-600 bg-blue-50 p-1.5 rounded-md text-sm" />
                  <span>Total ITC Available</span>
                </div>
                <div className="text-2xl font-bold text-[#1e293b] mt-2">{formatINR(gstr2Kpis.totalItcAvailable)}</div>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between">
                <div className="flex items-center gap-2 text-[#64748b] text-xs font-medium">
                  <i className="ri-building-line text-purple-600 bg-purple-50 p-1.5 rounded-md text-sm" />
                  <span>Registered Suppliers</span>
                </div>
                <div className="text-2xl font-bold text-[#1e293b] mt-2">{gstr2Kpis.registeredSuppliers}</div>
              </div>
            </div>

            {/* Purchase from Registered Suppliers */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
                <h3 className="text-sm font-bold text-[#1e293b]">Purchase from Registered Suppliers</h3>
                <button
                  onClick={handleExportExcel}
                  className="h-7 px-2.5 rounded border border-[#e2e8f0] bg-white text-[11px] text-[#475569] hover:bg-slate-50 flex items-center gap-1 cursor-pointer"
                >
                  <i className="ri-download-line text-xs" />
                  <span>Export CSV</span>
                </button>
              </div>

              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">
                      <th className="px-4 py-3">Invoice No</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Supplier GSTIN</th>
                      <th className="px-4 py-3">Supplier Name</th>
                      <th className="px-4 py-3 text-right">Taxable Value</th>
                      <th className="px-4 py-3 text-right">CGST</th>
                      <th className="px-4 py-3 text-right">SGST</th>
                      <th className="px-4 py-3 text-right">IGST</th>
                      <th className="px-4 py-3 text-right">Total GST</th>
                      <th className="px-4 py-3 text-right">Eligible ITC</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {registeredPurchases.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-4 py-3 font-medium text-[#4f46e5]">{r.invoiceNo}</td>
                        <td className="px-4 py-3 text-[#64748b]">{r.date}</td>
                        <td className="px-4 py-3 font-mono text-[#334155]">{r.supplierGstin}</td>
                        <td className="px-4 py-3 font-medium text-[#1e293b]">{r.supplierName}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#1e293b]">{formatINR(r.taxableValue)}</td>
                        <td className="px-4 py-3 text-right font-medium text-blue-600">{r.cgst ? formatINR(r.cgst) : '—'}</td>
                        <td className="px-4 py-3 text-right font-medium text-purple-600">{r.sgst ? formatINR(r.sgst) : '—'}</td>
                        <td className="px-4 py-3 text-right font-medium text-amber-600">{r.igst ? formatINR(r.igst) : '—'}</td>
                        <td className="px-4 py-3 text-right font-medium text-[#1e293b]">{formatINR(r.totalGst)}</td>
                        <td className="px-4 py-3 text-right font-bold text-emerald-600">{formatINR(r.eligibleItc)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#f8fafc] border-t-2 border-[#e2e8f0] font-bold text-xs text-[#1e293b]">
                      <td className="px-4 py-3" colSpan={4}>TOTAL ({registeredPurchasesSummary.count} invoices)</td>
                      <td className="px-4 py-3 text-right">{formatINR(registeredPurchasesSummary.taxableValue)}</td>
                      <td className="px-4 py-3 text-right text-blue-600">{formatINR(registeredPurchasesSummary.cgst)}</td>
                      <td className="px-4 py-3 text-right text-purple-600">{formatINR(registeredPurchasesSummary.sgst)}</td>
                      <td className="px-4 py-3 text-right text-amber-600">{formatINR(registeredPurchasesSummary.igst)}</td>
                      <td className="px-4 py-3 text-right">{formatINR(registeredPurchasesSummary.totalGst)}</td>
                      <td className="px-4 py-3 text-right text-emerald-600">{formatINR(registeredPurchasesSummary.eligibleItc)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Import of Goods */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-[#e2e8f0]">
                <h3 className="text-sm font-bold text-[#1e293b]">Import of Goods</h3>
              </div>

              <div className="p-8 text-center flex flex-col items-center justify-center">
                <i className="ri-ship-line text-4xl text-[#94a3b8] mb-2" />
                <p className="text-sm font-medium text-[#475569]">No import transactions recorded for this period</p>
                <p className="text-xs text-[#94a3b8] mt-0.5">Import GST (IGST on imports) will appear here when recorded</p>
              </div>

              {/* Bottom Summary Strip */}
              <div className="border-t border-[#e2e8f0] bg-slate-50/60 p-4 grid grid-cols-1 sm:grid-cols-4 gap-4 text-xs">
                <div>
                  <span className="text-[11px] text-[#64748b]">Total Purchase Taxable</span>
                  <div className="text-sm font-bold text-[#1e293b] mt-0.5">{formatINR(importSummary.totalPurchaseTaxable)}</div>
                </div>
                <div>
                  <span className="text-[11px] text-[#64748b]">Total ITC Available</span>
                  <div className="text-sm font-bold text-emerald-600 mt-0.5">{formatINR(importSummary.totalItcAvailable)}</div>
                </div>
                <div>
                  <span className="text-[11px] text-[#64748b]">CGST ITC / SGST ITC</span>
                  <div className="text-sm font-bold text-[#1e293b] mt-0.5">{formatINR(importSummary.cgstItc)} / {formatINR(importSummary.sgstItc)}</div>
                </div>
                <div>
                  <span className="text-[11px] text-[#64748b]">IGST ITC</span>
                  <div className="text-sm font-bold text-[#1e293b] mt-0.5">{formatINR(importSummary.igstItc)}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: GSTR-3B (Summary)                                                  */}
        {/* ========================================================================= */}
        {activeTab === 'gstr3b' && (
          <div className="space-y-6 animate-fade-in">
            {/* Net GST Payable / Refund Due Banner Card */}
            <div className="bg-emerald-50/80 border border-emerald-200 rounded-2xl p-5 flex items-center justify-between shadow-2xs">
              <div>
                <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Net GST Payable / Refund Due</span>
                <div className="text-2xl font-black text-emerald-700 mt-1">{gstr3bBanner.amountFormatted}</div>
                <p className="text-xs text-emerald-600/90 mt-0.5 font-medium">{gstr3bBanner.subtext}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-100/90 text-emerald-700 flex items-center justify-center text-2xl shadow-2xs">
                <i className="ri-bank-card-line" />
              </div>
            </div>

            {/* 3.1 Details of Outward Supplies */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-[#1e293b]">3.1 Details of Outward Supplies</h3>
                <button
                  onClick={handleExportExcel}
                  className="h-7 px-2.5 rounded border border-[#e2e8f0] bg-white text-[11px] text-[#475569] hover:bg-slate-50 flex items-center gap-1 cursor-pointer"
                >
                  <i className="ri-download-line text-xs" />
                  <span>Export CSV</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                {/* (a) Taxable Outward Supplies */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm space-y-3">
                  <span className="text-xs font-semibold text-[#1e293b] block">(a) Taxable Outward Supplies</span>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <span className="text-[10px] text-[#64748b] block">Integrated Tax (IGST)</span>
                      <span className="font-bold text-amber-600 mt-0.5 block">{formatINR(outward31.taxable.igst)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-[#64748b] block">Central Tax (CGST)</span>
                      <span className="font-bold text-blue-600 mt-0.5 block">{formatINR(outward31.taxable.cgst)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-[#64748b] block">State Tax (SGST)</span>
                      <span className="font-bold text-purple-600 mt-0.5 block">{formatINR(outward31.taxable.sgst)}</span>
                    </div>
                  </div>
                </div>

                {/* (b) Zero Rated & (c) Nil Rated */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex items-center justify-around">
                  <div className="text-center">
                    <span className="text-[11px] text-[#64748b] block">(b) Zero Rated Supplies</span>
                    <span className="font-bold text-[#1e293b] text-sm mt-1 block">₹{outward31.zeroRated}</span>
                  </div>
                  <div className="h-8 w-px bg-[#e2e8f0]" />
                  <div className="text-center">
                    <span className="text-[11px] text-[#64748b] block">(c) Nil Rated</span>
                    <span className="font-bold text-[#1e293b] text-sm mt-1 block">₹{outward31.nilRated}</span>
                  </div>
                </div>

                {/* (d) Exempted */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-center items-center">
                  <span className="text-[11px] text-[#64748b] block">(d) Exempted</span>
                  <span className="font-bold text-[#1e293b] text-sm mt-1 block">₹{outward31.exempted}</span>
                </div>
              </div>
            </div>

            {/* 4. Eligible Input Tax Credit (ITC) */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 shadow-sm space-y-4 text-xs">
              <h3 className="text-sm font-bold text-[#1e293b]">4. Eligible Input Tax Credit (ITC)</h3>

              <div className="space-y-2">
                <span className="text-xs font-semibold text-[#475569] block">(A) ITC Available</span>
                <div className="flex justify-between py-1.5 border-b border-[#f1f5f9]">
                  <span className="text-[#334155]">Import of Goods</span>
                  <span className="font-medium text-[#1e293b]">₹{itc4.importOfGoods}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[#f1f5f9]">
                  <span className="text-[#334155]">From Registered Suppliers (Inward Supplies)</span>
                  <span className="font-bold text-emerald-600">{formatINR(itc4.fromRegisteredSuppliers)}</span>
                </div>
              </div>

              <div className="space-y-2 pt-1">
                <span className="text-xs font-semibold text-[#475569] block">(B) ITC Reversed</span>
                <div className="flex justify-between py-1.5 border-b border-[#f1f5f9]">
                  <span className="text-[#334155]">As per rules 42 & 43 of CGST/SGST rules</span>
                  <span className="font-medium text-[#1e293b]">₹{itc4.itcReversed}</span>
                </div>
              </div>

              {/* (C) Net ITC Available */}
              <div className="bg-emerald-50/70 border border-emerald-100 rounded-lg p-3 flex justify-between items-center">
                <span className="font-bold text-emerald-900">(C) Net ITC Available (A - B)</span>
                <span className="text-base font-extrabold text-emerald-700">{formatINR(itc4.netItcAvailable)}</span>
              </div>
            </div>

            {/* 5. Values of Exempt / Nil / Non-GST Supplies */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 shadow-sm text-xs">
              <h3 className="text-sm font-bold text-[#1e293b]">5. Values of Exempt / Nil / Non-GST Supplies</h3>
              <div className="flex justify-between items-center mt-3 pt-2 border-t border-[#f1f5f9]">
                <span className="text-[#64748b]">Inter-state and intra-state supplies to unregistered persons</span>
                <span className="font-bold text-[#1e293b]">₹0</span>
              </div>
            </div>

            {/* 6. Payment of Tax */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-[#e2e8f0]">
                <h3 className="text-sm font-bold text-[#1e293b]">6. Payment of Tax</h3>
              </div>

              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold uppercase tracking-wider">
                      <th className="px-6 py-3 text-[#64748b]">Description</th>
                      <th className="px-6 py-3 text-right text-amber-600">IGST</th>
                      <th className="px-6 py-3 text-right text-blue-600">CGST</th>
                      <th className="px-6 py-3 text-right text-purple-600">SGST</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {payment6.map((row, i) => (
                      <tr key={i} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-6 py-3.5 font-medium text-[#1e293b]">{row.description}</td>
                        <td className="px-6 py-3.5 text-right font-medium text-amber-700">
                          {row.igst !== null ? formatINR(row.igst) : '—'}
                        </td>
                        <td className="px-6 py-3.5 text-right font-medium text-blue-700">
                          {row.cgst !== null ? formatINR(row.cgst) : '—'}
                        </td>
                        <td className="px-6 py-3.5 text-right font-medium text-purple-700">
                          {row.sgst !== null ? formatINR(row.sgst) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
